/**
 * Module: screens/creator/_chrome/WizardShell
 * Role: Wizard-local chrome (FTR-1131 amendment 2026-06-07).
 *       Replaces the tab-shell PageHeader/CtaBar atoms which carry Mark+wordmark
 *       and lack the design's centered-title + sticky-CTA layout. SCOPE: creator
 *       onboarding wizard only — do NOT export from `components/atoms/`, do NOT
 *       reuse on Home/Tasks/Profile (those want the Awardy wordmark).
 *
 *       Pixel-for-pixel port of `ai/designs/miniapp/project/creator-onboarding.jsx`
 *       (bundle Eqq6AbBPpm1zPPyErlkgJQ, chat16 founder approval):
 *         - WizardFrame    — flex-column root pinned over IOSScreen's status-bar
 *                            padding so CTA can be sticky-bottom.
 *         - WizardHeader   — chevron-only GlassPill (no Mark, no wordmark) +
 *                            absolutely-centered title (design L46-62).
 *         - WizardStepStrip — coloured bar segments + «ШАГ N ИЗ M» caption
 *                            (design L25-44).
 *         - WizardCtaBar   — `padding: '10px 16px 20px'` wrapper + PressBtn
 *                            with explicit `bg=P.terracotta` (design L65-72).
 *
 * Uses: react + components/atoms/{GlassPill,WizardCtaBar} + styles/tokens.
 * Used by: all creator wizard steps + StatusScreen pending branch.
 * Glossary: ai/glossary/buyer.md
 */

import { CSSProperties, ReactNode } from 'react';
import { GlassPill } from '../../../components/atoms/GlassPill';
import { WizardCtaBar, type WizardCtaBarProps } from '../../../components/atoms/WizardCtaBar';
import { P, S, T } from '../../../styles/tokens';

// ── WizardFrame ────────────────────────────────────────────────────────────
// IOSScreen wraps content in `position:absolute, overflowY:auto` — fine for
// scrollable feed pages, but makes flex-1/sticky-bottom impossible. Wizard
// steps need flex-column root so CTA can be sticky at the bottom.
export interface WizardFrameProps {
  children: ReactNode;
}

export function WizardFrame({ children }: WizardFrameProps) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: P.cream,
        // Top inset reserved for TG safe area / status bar, mirroring the
        // 54px IOSScreen wraps everything else in (`IOSScreen.tsx:52`).
        paddingTop: 'env(safe-area-inset-top, 0px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {children}
    </div>
  );
}

// ── WizardHeader ───────────────────────────────────────────────────────────
// Design (`creator-onboarding.jsx:46-62`): 44-min row with a 40px chevron
// GlassPill on the left and the title absolutely centered. No brand mark, no
// wordmark — those belong to the tab-shell header on Home/Tasks/Profile.
export interface WizardHeaderProps {
  /** Centered title. Omit on PromoScreen where the design ships header-with-
   *  back-only (`creator-onboarding.jsx:133-137`). */
  title?: string;
  /** Mandatory in this wizard — without it the screen is the «trap» founder
   *  reported on 2026-06-07. Pass `() => navigate(-1)` for inner steps,
   *  `() => navigate('/profile')` for entry/exit screens. */
  onBack: () => void;
}

function ChevronLeft({ color = P.ink }: { color?: string }) {
  return (
    <svg width={11} height={18} viewBox="0 0 11 18" fill="none" aria-hidden="true">
      <path
        d="M9 1L2 9l7 8"
        stroke={color}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function WizardHeader({ title, onBack }: WizardHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        minHeight: 44,
        padding: '6px 16px 0',
        gap: 10,
        position: 'relative',
        flexShrink: 0,
      }}
    >
      <GlassPill
        onClick={onBack}
        variant="light"
        size={40}
        style={{ padding: 0, width: 40, flexShrink: 0 }}
      >
        <ChevronLeft color={P.ink} />
      </GlassPill>
      {title && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            textAlign: 'center',
            font: T.headline,
            color: S.label,
            // Title is decorative — never intercept back-pill taps.
            pointerEvents: 'none',
          }}
        >
          {title}
        </div>
      )}
    </div>
  );
}

// ── WizardStepStrip ────────────────────────────────────────────────────────
// Design (`creator-onboarding.jsx:25-44`): segmented bar + «ШАГ N ИЗ M»
// caption below. The plain StepStrip atom we ship at `screens/flow/StepStrip`
// only renders the bars — founder report 2026-06-07 specifically called out
// «шаги подписаны — Один из восьми, два из восьми — не применено».
export interface WizardStepStripProps {
  current: number;
  total: number;
}

const TRACK_INACTIVE = '#E8E3D4'; // mirrors `screens/flow/StepStrip.tsx`

export function WizardStepStrip({ current, total }: WizardStepStripProps) {
  return (
    <div
      style={{
        padding: '12px 16px 4px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', gap: 4 }}>
        {Array.from({ length: total }).map((_, i) => {
          const filled = i + 1 <= current;
          return (
            <div
              key={i}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                background: filled ? P.terracotta : TRACK_INACTIVE,
                transition: 'background 0.3s ease',
              }}
            />
          );
        })}
      </div>
      <div
        style={{
          font: T.caption,
          color: S.tertiaryLabel,
          letterSpacing: 0.2,
          textTransform: 'uppercase',
        }}
      >
        ШАГ {current} ИЗ {total}
      </div>
    </div>
  );
}

// ── WizardBody ─────────────────────────────────────────────────────────────
// Scrollable middle section between header/strip and CTA. flex:1 + own scroll.
export interface WizardBodyProps {
  children: ReactNode;
  style?: CSSProperties;
}

export function WizardBody({ children, style }: WizardBodyProps) {
  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '0 16px 8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── WizardCtaBar ───────────────────────────────────────────────────────────
// Moved to components/atoms/WizardCtaBar — re-exported here for backward compat.
export { WizardCtaBar, type WizardCtaBarProps };
